import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { Search, Users } from 'lucide-react';

// Mirrors the mobile supervisor "My Team" view: direct reports from /users/my-team,
// joined client-side with live inside/outside status from /location-state/employee-status.
const LOCATION = {
  inside: { label: 'Inside', className: 'badge-success' },
  outside: { label: 'Outside', className: 'badge-warn' },
  unknown: { label: 'Unknown', className: 'badge-muted' },
};

export default function MyTeam() {
  const [team, setTeam] = useState([]);
  const [locationMap, setLocationMap] = useState({});
  const [q, setQ] = useState('');
  const [loaded, setLoaded] = useState(false);

  const loadTeam = async () => {
    try {
      const { data } = await api.get('/users/my-team');
      setTeam(data);
    } catch {}
    setLoaded(true);
  };

  const loadLocation = async () => {
    try {
      const { data } = await api.get('/location-state/employee-status');
      const map = {};
      data.forEach((s) => { map[s.employee_id] = s; });
      setLocationMap(map);
    } catch {}
  };

  useEffect(() => {
    loadTeam();
    loadLocation();
    const id = setInterval(loadLocation, 15000);
    return () => clearInterval(id);
  }, []);

  const statusOf = (u) => {
    const loc = locationMap[u.id];
    if (!loc || loc.flagged) return 'unknown';
    return loc.current_status || 'unknown';
  };

  const filtered = team.filter((u) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [u.name, u.email, u.department, u.employee_code]
      .some((v) => (v || '').toLowerCase().includes(s));
  });

  const insideCount = team.filter((u) => statusOf(u) === 'inside').length;

  return (
    <div className="space-y-6" data-testid="my-team-page">
      <header>
        <div className="overline">Module 06 · Supervisor</div>
        <h1 className="heading text-3xl font-black text-ink">My Team</h1>
        <p className="text-sm text-zinc-500 mt-1">
          {team.length} team member{team.length !== 1 ? 's' : ''} · {insideCount} currently inside
        </p>
      </header>

      <div className="card p-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            className="input pl-9"
            placeholder="Search by name, email, department, code…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="team-search"
          />
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200 text-left">
                <th className="px-4 py-3 overline">Name</th>
                <th className="px-4 py-3 overline">Email</th>
                <th className="px-4 py-3 overline">Department</th>
                <th className="px-4 py-3 overline">Employee Code</th>
                <th className="px-4 py-3 overline">Location</th>
              </tr>
            </thead>
            <tbody data-testid="team-table-body">
              {loaded && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-zinc-400">
                    <Users className="mx-auto mb-2 text-zinc-300" size={32} />
                    {team.length === 0 ? 'No team members assigned to you yet.' : 'No team members match your search.'}
                  </td>
                </tr>
              )}
              {filtered.map((u) => {
                const loc = LOCATION[statusOf(u)];
                return (
                  <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`team-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-ink">{u.name}</span>
                        {!u.active && <span className="badge-danger">Inactive</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-600">{u.email}</td>
                    <td className="px-4 py-3 text-zinc-600">{u.department || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-600">{u.employee_code || '—'}</td>
                    <td className="px-4 py-3"><span className={loc.className}>{loc.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
